// @flow
import * as React from 'react';
import invariant from 'invariant';
import styled, { keyframes } from 'styled-components';
import { identity, isFunction, deepFlatten, isDefined } from 'utils/functional';
import { withRouter } from 'react-router-dom';
import type { RouterHistory } from 'react-router-dom';
import { trackPageView } from 'utils/analytics';
import usePrevious from 'utils/usePrevious';
import {
  GeneralError,
  FormContext,
  SubmitButton,
  SubmitLink,
  Section as FormSection
} from 'components/Form';
import Divider from 'components/Divider';
import Box from 'components/Box';
import Icon from 'components/Icon';
import { LinkButton, SecondaryButton } from 'components/Button';
import { H4, BodySmall } from 'components/Text';
import ToggleHelperContainer from 'containers/ToggleHelperContainer';
import TabContext from 'containers/TabContext';
import DefaultHeader from './Header';
import SmallHeader from './SmallHeader';
import { transition, colors, fontWeights, gutterWidth } from '../../../theme';

const { useRef, useEffect, useContext } = React;

type Row = {
  toggle?: string,
  fields: Array<string>,
  fieldsToValidate?: Array<string>,
  noColumns?: boolean,
  columns?: number,
  toggleQaId?: string
};

type StepSection = {
  title?: string,
  intro?: React.Node,
  rows: Array<?Row>,
  showGeneralErrors?: boolean,
  matchError?: string
};

type Step = {
  title?: string,
  eventName?: string,
  sections: Array<StepSection>,
  nextText?: string,
  belowButtons?: React.Node,
  ignoreInNav?: boolean,
  loadingSection?: React.Node,
  footer?: React.Node
};

export type Steps = Array<Step>;

const getFieldsToValidateByStep = (steps: Steps, index: number) => {
  const fields = steps
    .filter((step, i) => i <= index)
    .map(step =>
      deepFlatten(
        step.sections.map(section =>
          section.rows
            .map(row => (!row ? [] : row.fieldsToValidate || row.fields))
            .filter(identity)
        )
      )
    );
  return fields;
};

const getFieldsToValidate = (steps: Steps, index: number) =>
  deepFlatten(getFieldsToValidateByStep(steps, index)).filter(identity);

const getPreviousErrorsByStep = ({ steps, currentStep, errors }) => {
  const errorsByStep = getFieldsToValidateByStep(steps, currentStep - 1);
  return errorsByStep.map(step =>
    Object.keys(errors).filter(errorName => step.includes(errorName))
  );
};

const ToggleLink = styled.div`
  padding-bottom: 31px;
  &::before {
    content: '+ ';
    color: ${colors.waterBlue};
    font-weight: ${fontWeights.semiBold};
  }
`;

const ToggleSection = styled(
  ({
    children,
    title,
    className,
    toggleQaId
  }: {
    children: React.Node,
    title?: string,
    className: string,
    toggleQaId?: string
  }) =>
    isDefined(title) ? (
      <ToggleHelperContainer>
        {(toggled, toggle) => (
          <Box position="relative" pt={31} className={className}>
            <Box>
              {toggled ? (
                children
              ) : (
                <ToggleLink>
                  <LinkButton onClick={toggle} data-qa-id={toggleQaId}>
                    {title}
                  </LinkButton>
                </ToggleLink>
              )}
            </Box>
          </Box>
        )}
      </ToggleHelperContainer>
    ) : (
      children
    )
)`
  &:last-child {
    &::after {
      opacity: 0;
    }
  }
`;

const StyledSubmitButton = styled(SubmitButton)`
  svg {
    transition: transform ${transition};
  }
  &:hover,
  &:focus {
    svg {
      transform: translateX(50%);
    }
  }
`;

const StyledForm = styled.div`
  ${ToggleSection} {
    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      width: 100%;
      height: 1px;
      background: ${colors.aluminum};
    }
    &::before {
      top: 0;
    }
    &::after {
      bottom: 0;
    }
    & + ${ToggleSection} {
      &::before {
        opacity: 0;
      }
    }
    & + ${`*:not(${ToggleSection})`} {
      margin-top: 31px;
    }
  }
`;

type SidebarSection = {
  imageUrl?: string,
  body?: React.Node
};

const aFloat = keyframes`
	0% {
		transform: translate(0,  0px) rotateZ(0);
	}
	50% {
		transform: translate(0, -6px) rotateZ(0);
	}
	100% {
		transform: translate(0, -0px) rotateZ(0);
	}
`;

const aFloatShadow = keyframes`
	0% {
		transform: translateX(-50%) scale(1, 1) rotateZ(0);
	}
	50% {
		transform: translateX(-50%) scale(0.8, 0.9) rotateZ(0);
	}
	100% {
		transform: translateX(-50%) scale(1, 1) rotateZ(0);
	}
`;

const SidebarImage = styled(Box)`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    display: block;
    width: 50%;
    padding-bottom: 10%;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.04);
    animation: ${aFloatShadow} 3s cubic-bezier(0.445, 0.05, 0.55, 0.95) infinite;
    will-change: transform;
  }
  img {
    position: relative;
    width: 100%;
    animation: ${aFloat} 3s cubic-bezier(0.445, 0.05, 0.55, 0.95) infinite;
    will-change: transform;
  }
`;

const FormStep = styled(Box).attrs(({ $active }) => ({
  position: $active ? 'relative' : 'absolute',
  zIndex: $active ? 1 : 0,
  visibility: $active ? 'visible' : 'hidden',
  overflow: $active ? 'visible' : 'hidden',
  height: $active ? 'auto' : 0,
  opacity: $active ? 1 : 0,
  pointerEvents: $active ? 'auto' : 'none'
}))`
  top: 0;
  left: 0;
  width: 100%;
  transition: all ${transition};
`;

const PreviousErrorsSection = styled(FormSection)`
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 0;
    transform: translateY(5px);
    width: 100%;
    height: 100%;
    background: ${colors.scarlet};
    transition: all ${transition};
  }
`;

const Field = ({ field, fields }) => {
  if (!field) {
    return null;
  }
  const f = fields[field];
  invariant(f, `Field with name '${field}' does not exist`);
  return isFunction(f) ? f() : f;
};

const SectionFields = ({
  row,
  fields,
  seenFields
}: {
  row: Row,
  fields: { [string]: React.Node | (() => React.Node) },
  seenFields: { [string]: number }
}) => (
  <Box display="flex" flexWrap="wrap" mx="-8px" width="calc(100% + 16px)">
    {row.fields.map((fieldName, fieldIndex, rowFields) => {
      const columns =
        row && row.noColumns
          ? 1
          : row && isDefined(row.columns)
          ? row.columns
          : rowFields.length;
      const fieldCount = seenFields[fieldName] || 0;
      seenFields[fieldName] = fieldCount + 1;
      return (
        <Box
          key={fieldCount === 0 ? fieldName : `${fieldName}-${fieldCount}`}
          width={[1, null, null, 1 / columns]}
          px="8px"
          flex="0 0 auto"
        >
          <Field fields={fields} field={fieldName} />
        </Box>
      );
    })}
  </Box>
);

const Container = ({ hasSidebar, ...props }: { hasSidebar: boolean }) =>
  hasSidebar ? (
    <Box display="flex" {...props} />
  ) : (
    <React.Fragment {...props} />
  );

const MultiStepForm = ({
  steps: stepsProp,
  fields,
  submitting,
  errors,
  updateValues,
  currentStep = 0,
  allowDraft,
  goBackToStep,
  history,
  intro,
  title,
  singlePage,
  noButtons,
  sidebar,
  logo,
  successSection,
  submitText,
  hasTracking = false,
  hideHeader
}: {
  steps: Steps,
  fields: { [string]: React.Node | (() => React.Node) },
  submitting: boolean,
  errors?: APIErrors,
  updateValues: Object => void,
  currentStep?: number,
  allowDraft?: boolean,
  goBackToStep?: Function,
  history: RouterHistory,
  intro?: React.Node,
  title?: string,
  singlePage?: boolean,
  noButtons?: boolean,
  sidebar?: Array<SidebarSection>,
  logo?: React.Node,
  successSection?: React.Node,
  submitText?: string,
  hasTracking?: boolean,
  hideHeader?: boolean
}) => {
  const steps = singlePage
    ? stepsProp.reduce(
        (acc, { title: stepTitle, sections }) => {
          const s = acc[0];
          if (stepTitle && !(sections.length && sections[0].title)) {
            sections[0].title = stepTitle;
          }
          return [{ sections: s.sections.concat(sections) }];
        },
        [{ sections: [] }]
      )
    : stepsProp;

  const stepRef = useRef(null);
  const formRef = useRef(null);

  const { edited } = useContext(FormContext) || {};
  const isTabbing = useContext(TabContext) || false;
  const prevState = usePrevious({ isTabbing, currentStep });

  const hasSidebar = !!sidebar;

  const Header = hasSidebar ? SmallHeader : DefaultHeader;
  const Section = hasSidebar ? 'div' : FormSection;

  useEffect(
    () => {
      if (!(!submitting && !singlePage && edited)) return;
      const beforeUnload = e => {
        e.returnValue = getText(
          'Are you sure you want to go back? Your data will not be saved.'
        );
      };
      window.addEventListener('beforeunload', beforeUnload);
      return () => window.removeEventListener('beforeunload', beforeUnload);
    },
    [edited, singlePage, submitting]
  );

  const stepEventName =
    steps[currentStep].eventName || `Multi-Step Form: Step ${currentStep}`;

  useEffect(
    () => {
      if (hasTracking) {
        trackPageView(stepEventName, { step_index: currentStep });
      }
    },
    [currentStep, hasTracking, stepEventName]
  );

  useEffect(
    () => {
      if (stepRef && stepRef.current) {
        const inputsList: NodeList<HTMLElement> = stepRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]'
        );
        const inputs = Array.prototype.slice.call(inputsList).filter(input => {
          const noTabAttr = input.getAttribute('data-no-tab');
          return !noTabAttr || noTabAttr !== 'true';
        });
        if (formRef && formRef.current) {
          formRef.current
            .querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            .forEach((el: HTMLElement) => {
              el.tabIndex = -1;
            });
          inputs.forEach((el: HTMLElement) => {
            el.tabIndex = 0;
          });
        }
        if (isTabbing && prevState && currentStep !== prevState.currentStep) {
          inputs[0].focus();
        }
      }
    },
    [currentStep, isTabbing, prevState]
  );
  const previousErrorsByStep: Array<Array<string>> = errors
    ? getPreviousErrorsByStep({
        steps,
        currentStep,
        errors
      })
    : [];
  const previousErrors = deepFlatten(previousErrorsByStep);

  const goBackTo = index => {
    if (goBackToStep) {
      goBackToStep(index);
    }
  };

  const seenFields = {};
  return (
    <Container hasSidebar={hasSidebar}>
      <Box
        maxWidth={sidebar ? 856 : 'none'}
        height="100%"
        flex="1 1 auto"
        m="0 auto"
        px={
          hasSidebar && [
            gutterWidth / 2,
            null,
            null,
            gutterWidth,
            gutterWidth * 2
          ]
        }
      >
        <Box
          component={StyledForm}
          minHeight={hasSidebar && 'calc(100vh - 150px)'}
          height="100%"
        >
          <div ref={formRef}>
            <Box
              position="relative"
              width={1}
              minHeight="100vh"
              display="flex"
              flexDirection="column"
              justifyContent="stretch"
            >
              {!hideHeader && (
                <Box
                  mb={hasSidebar ? 36 : 0}
                  bg="white"
                  position="relative"
                  zIndex={2}
                >
                  <Header
                    steps={steps}
                    currentStep={currentStep}
                    goBackToStep={goBackTo}
                    goBack={history.goBack}
                    errors={previousErrorsByStep}
                    title={title}
                    edited={edited}
                    logo={logo}
                  />
                </Box>
              )}
              <Box position="relative" flex="1 1 auto">
                {successSection ||
                  steps.map((step, stepIndex) =>
                    stepIndex === currentStep ? (
                      <FormStep
                        $active={currentStep === stepIndex}
                        key={step.title || stepIndex}
                        bg={hasSidebar && 'white'}
                      >
                        {step.loadingSection || (
                          <div ref={currentStep === stepIndex ? stepRef : null}>
                            {isDefined(intro) && stepIndex === 0 && intro}
                            {previousErrors.length > 0 && (
                              <PreviousErrorsSection large>
                                <H4>{getText('Previous Errors')}</H4>
                                <BodySmall mb={40}>
                                  {getText(
                                    'We found these errors on previous pages. You may fix them here, or go back to make further changes.'
                                  )}
                                </BodySmall>
                                {previousErrors.map(error => fields[error])}
                              </PreviousErrorsSection>
                            )}

                            <GeneralError
                              ignore={step.sections.map(
                                ({ matchError }) => matchError
                              )}
                            />

                            {step.sections.map((section, sectionIndex) => {
                              const lastStep = stepIndex === steps.length - 1;
                              return (
                                <Section
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={sectionIndex}
                                  large={hasSidebar ? undefined : !singlePage}
                                  title={hasSidebar ? undefined : section.title}
                                  description={
                                    hasSidebar ? undefined : section.intro
                                  }
                                >
                                  {hasSidebar && (
                                    <>
                                      <H4>{section.title}</H4>

                                      {section.intro && (
                                        <Box mb={24}>{section.intro}</Box>
                                      )}
                                    </>
                                  )}

                                  {(section.matchError ||
                                    section.showGeneralErrors) && (
                                    <GeneralError match={section.matchError} />
                                  )}

                                  {section.rows.map((row: ?Row, rowIndex) =>
                                    row ? (
                                      <ToggleSection
                                        title={row.toggle}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={rowIndex}
                                        toggleQaId={row.toggleQaId}
                                      >
                                        <SectionFields
                                          row={row}
                                          fields={fields}
                                          seenFields={seenFields}
                                        />
                                      </ToggleSection>
                                    ) : (
                                      <Divider
                                        my={54}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={rowIndex}
                                      />
                                    )
                                  )}

                                  {sectionIndex === step.sections.length - 1 &&
                                    stepIndex === currentStep &&
                                    !noButtons && (
                                      <>
                                        <Box
                                          display={[
                                            'block',
                                            null,
                                            null,
                                            'flex'
                                          ]}
                                          width="calc(100% + 16px)"
                                          mx="-8px"
                                          mt={
                                            hasSidebar
                                              ? gutterWidth
                                              : [
                                                  gutterWidth,
                                                  null,
                                                  null,
                                                  gutterWidth * 2
                                                ]
                                          }
                                        >
                                          {stepIndex > 0 && goBackToStep && (
                                            <Box width="100%" p="8px">
                                              <SecondaryButton
                                                square={hasSidebar}
                                                size="large"
                                                blue
                                                width="100%"
                                                p="15px 0"
                                                onClick={() => {
                                                  goBackToStep(stepIndex - 1);
                                                }}
                                                data-qa-id="multi-form-prev-step"
                                              >
                                                {getText('Back')}
                                              </SecondaryButton>
                                            </Box>
                                          )}
                                          <Box width="100%" p="8px">
                                            {lastStep ? (
                                              <StyledSubmitButton
                                                square={hasSidebar}
                                                size="large"
                                                width="100%"
                                                p="15px 0"
                                                onClick={() =>
                                                  updateValues({
                                                    publish: true,
                                                    nextStep: null,
                                                    validate: getFieldsToValidate(
                                                      steps,
                                                      stepIndex
                                                    )
                                                  })
                                                }
                                              >
                                                {step.nextText ||
                                                  submitText ||
                                                  getText('Finish & Publish')}
                                                <Icon
                                                  name="arrow"
                                                  width={14}
                                                  height={14}
                                                  ml={12}
                                                  fill="currentColor"
                                                />
                                              </StyledSubmitButton>
                                            ) : (
                                              <StyledSubmitButton
                                                square={hasSidebar}
                                                size="large"
                                                width="100%"
                                                p="15px 0"
                                                onClick={() => {
                                                  updateValues({
                                                    publish: false,
                                                    nextStep: stepIndex + 1,
                                                    validate: getFieldsToValidate(
                                                      steps,
                                                      stepIndex
                                                    )
                                                  });
                                                }}
                                                data-qa-id="multi-form-next-step"
                                              >
                                                {step.nextText ||
                                                  getText('Next')}
                                                <Icon
                                                  name="arrow"
                                                  width={14}
                                                  height={14}
                                                  ml={12}
                                                  fill="currentColor"
                                                />
                                              </StyledSubmitButton>
                                            )}
                                          </Box>
                                        </Box>
                                        {lastStep && allowDraft && (
                                          <Box mt={36} textAlign="center">
                                            <SubmitLink
                                              data-qa-id="save-draft"
                                              onClick={() =>
                                                updateValues({
                                                  publish: false,
                                                  nextStep: null,
                                                  validate: getFieldsToValidate(
                                                    steps,
                                                    stepIndex
                                                  )
                                                })
                                              }
                                            >
                                              {getText('Save as Draft')}
                                            </SubmitLink>
                                          </Box>
                                        )}
                                        {step.belowButtons && (
                                          <Box mt={24} textAlign="center">
                                            {step.belowButtons}
                                          </Box>
                                        )}
                                      </>
                                    )}
                                </Section>
                              );
                            })}
                          </div>
                        )}
                      </FormStep>
                    ) : null
                  )}
              </Box>

              {steps[currentStep] && steps[currentStep].footer && (
                <Box my={[48, null, null, 64]}>{steps[currentStep].footer}</Box>
              )}
            </Box>
          </div>
        </Box>
      </Box>

      {sidebar && (
        <Box
          display={['none', null, null, 'block']}
          width={1 / 3}
          maxWidth={572}
          flex="0 0 auto"
        >
          <Box
            position="fixed"
            width={1 / 3}
            maxWidth={572}
            minHeight="100vh"
            display="flex"
            alignItems="center"
            bg={colors.fog}
          >
            {sidebar.map((section, index) =>
              index === currentStep ? (
                <Box
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  width={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  px={[gutterWidth / 2, null, null, gutterWidth]}
                  pt={gutterWidth / 2}
                  pb={(gutterWidth * 3) / 2}
                  textAlign="center"
                >
                  <SidebarImage width={270} maxWidth="100%" mx="auto">
                    <img src={section.imageUrl} />
                  </SidebarImage>

                  {section.body && (
                    <Box
                      mt={36}
                      maxWidth={400}
                      color={colors.battleshipGrey}
                      component={BodySmall}
                    >
                      {section.body}
                    </Box>
                  )}
                </Box>
              ) : null
            )}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default withRouter(MultiStepForm);
